import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiErrorResponse, ApiResponse } from '../../types';
import {
  apiDataResponseMapper,
  apiPayloadMapper,
  dynamicFieldsPayloadMapper,
} from '../../utils';
import api from '../../api';
import type {
  DeleteTerrainCleanupPayload,
  GetTerrainCleanupsPayload,
  RenameTerrainCleanupPayload,
  TerrainCleanupListResponse,
  TerrainCleanupResponse,
  CreateTerrainCleanupPayload,
} from './types';

// API Functions.
const createTerrainCleanupRequest = async (
  payload: CreateTerrainCleanupPayload,
): Promise<ApiResponse> => {
  const res = await api.post<ApiResponse>(
    `iterations/terrain-cleanups/`,
    apiPayloadMapper(payload),
  );

  return apiDataResponseMapper(res);
};

const getTerrainCleanupsRequest = async (
  payload: GetTerrainCleanupsPayload,
): Promise<TerrainCleanupListResponse> => {
  const dynamicFieldsPayload = dynamicFieldsPayloadMapper({
    excludeFields: ['aoiGeometry'],
  });
  const mappedPayload = apiPayloadMapper(payload);
  const res = await api.get<TerrainCleanupListResponse>(
    '/iterations/terrain-cleanups/',
    {
      params: {
        ...mappedPayload,
        ...dynamicFieldsPayload,
      },
    },
  );
  return apiDataResponseMapper(res.data);
};

const getTerrainCleanupRequest = async (
  cleanupId: string,
): Promise<ApiResponse<TerrainCleanupResponse>> => {
  const res = await api.get<ApiResponse<TerrainCleanupResponse>>(
    `/iterations/terrain-cleanups/${cleanupId}/`,
  );

  return apiDataResponseMapper(res.data);
};

const renameTerrainCleanupRequest = async (
  payload: RenameTerrainCleanupPayload,
): Promise<ApiResponse<TerrainCleanupResponse>> => {
  const { cleanupId, ...bodyPayload } = payload;
  const res = await api.patch<ApiResponse<TerrainCleanupResponse>>(
    `/iterations/terrain-cleanups/${cleanupId}/`,
    apiPayloadMapper(bodyPayload),
  );

  return apiDataResponseMapper(res.data);
};

const deleteTerrainCleanupRequest = async (
  payload: DeleteTerrainCleanupPayload,
): Promise<ApiResponse> => {
  const res = await api.delete<ApiResponse>(
    `/iterations/terrain-cleanups/${payload.cleanupId}/`,
  );
  return apiDataResponseMapper(res.data);
};

// Hooks.
export const useTerrainCleanups = (
  payload: GetTerrainCleanupsPayload,
  enabled = true,
) =>
  useQuery<TerrainCleanupListResponse, ApiErrorResponse>({
    queryKey: ['terrainCleanups', payload.iterationId],
    queryFn: () => getTerrainCleanupsRequest(payload),
    enabled,
  });

export const useTerrainCleanup = (cleanupId: string, enabled = true) =>
  useQuery<ApiResponse<TerrainCleanupResponse>, ApiErrorResponse>({
    queryKey: ['terrainCleanup', cleanupId],
    queryFn: () => getTerrainCleanupRequest(cleanupId),
    enabled,
  });

export const useCreateTerrainCleanup = () =>
  useMutation<ApiResponse, ApiErrorResponse, CreateTerrainCleanupPayload>({
    mutationFn: createTerrainCleanupRequest,
  });

export const useRenameTerrainCleanup = () =>
  useMutation<
    ApiResponse<TerrainCleanupResponse>,
    ApiErrorResponse,
    RenameTerrainCleanupPayload
  >({
    mutationFn: renameTerrainCleanupRequest,
  });

export const useDeleteTerrainCleanup = () =>
  useMutation<ApiResponse, ApiErrorResponse, DeleteTerrainCleanupPayload>({
    mutationFn: deleteTerrainCleanupRequest,
  });
